/**
 * Download Utility Functions
 * Handles file downloads for scalers and models
 */

import { scalersApi } from '@/api';

export interface ScalersData {
    session_id: string;
    scalers: unknown;
    downloaded_at: string;
    metadata?: unknown;
    format: string;
}

/**
 * Download a blob as a file
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Download scalers as ZIP file
 */
export const downloadScalersZip = async (
    sessionId: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const blob = await scalersApi.downloadScalersZip(sessionId);
        downloadBlob(blob, `scalers_${sessionId}.zip`);
        return { success: true };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
        };
    }
};

/**
 * Download scalers as JSON (fallback for serialized version)
 */
export const downloadScalersJson = (data: ScalersData): void => {
    const scalersData: ScalersData = {
        session_id: data.session_id,
        scalers: data.scalers,
        downloaded_at: new Date().toISOString(),
        metadata: data.scalers,
        format: 'serialized_python_objects'
    };

    const blob = new Blob([JSON.stringify(scalersData, null, 2)], {
        type: 'application/json'
    });

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    downloadBlob(blob, `scalers_${data.session_id}_${timestamp}.json`);
};

/**
 * Check if error is related to serialization issues
 */
export const isSerializationError = (errorMsg: string): boolean => {
    return errorMsg.includes('not JSON serializable');
};
